import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import type { OrderBy } from '@/lib/graphql-query-builder';
import { MIN_CATEGORIES_WITH_PEERS } from '@/modules/shared/hr-form-engine';
import { REVIEW_QUERY_KEYS, ReviewFilterValue } from '@/modules/performance/hr-review-core';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  forwardPeerReview,
  ForwardPeerReviewParams,
  getPeerRequestDetails,
  getPeerRequests,
  PeerSelectionError,
  savePeerSelection,
  SavePeerSelectionParams,
} from '../services/peer-request.service';

const PEER_REQUESTS_KEY = 'hr-peer-requests';

export const useGetPeerRequests = (
  managerId: string,
  filters: ReviewFilterValue,
  paging: {
    pageNo: number;
    pageSize: number;
    order?: OrderBy[];
    columnWhere?: Record<string, unknown>;
  }
) =>
  useGlobalQuery({
    queryKey: [PEER_REQUESTS_KEY, managerId, filters, paging],
    queryFn: () => getPeerRequests(managerId, filters, paging),
    enabled: Boolean(managerId),
    staleTime: 30 * 1000,
  });

export const useGetPeerRequestDetails = (pmReviewId?: string) =>
  useGlobalQuery({
    queryKey: [PEER_REQUESTS_KEY, 'details', pmReviewId],
    queryFn: () => getPeerRequestDetails(pmReviewId as string),
    enabled: Boolean(pmReviewId),
  });

/** Business-rule failures carry a translation key; anything else falls back to a generic message. */
const useMutationErrorToast = () => {
  const { toast } = useToast();
  const { t } = useTranslation();

  return (error: unknown) => {
    const key = error instanceof PeerSelectionError ? error.key : 'SOMETHING_WENT_WRONG';
    const detail = error instanceof PeerSelectionError ? error.detail : undefined;
    toast({
      variant: 'destructive',
      title: t(key, { count: MIN_CATEGORIES_WITH_PEERS, category: detail }),
    });
  };
};

const useInvalidateReviews = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      [
        PEER_REQUESTS_KEY,
        REVIEW_QUERY_KEYS.reviews,
        REVIEW_QUERY_KEYS.review,
        REVIEW_QUERY_KEYS.details,
        REVIEW_QUERY_KEYS.reviewDetails,
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey: [queryKey] }))
    );
};

export const useSavePeerSelection = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const invalidate = useInvalidateReviews();
  const showError = useMutationErrorToast();

  return useGlobalMutation({
    mutationFn: (params: SavePeerSelectionParams) => savePeerSelection(params),
    suppressToast: true,
    onSuccess: async () => {
      await invalidate();
      toast({ title: t('PEERS_SAVED') });
    },
    onError: showError,
  });
};

export const useForwardPeerReview = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const invalidate = useInvalidateReviews();
  const showError = useMutationErrorToast();

  return useGlobalMutation({
    mutationFn: (params: ForwardPeerReviewParams) => forwardPeerReview(params),
    suppressToast: true,
    onSuccess: async () => {
      await invalidate();
      toast({ title: t('PEER_REVIEW_FORWARDED') });
    },
    onError: showError,
  });
};
